import { Pencil, Trash2 } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { alertAPI } from '../../services/api';
import '../../styles/Alert.css';
import '../../styles/AlertConsole.css';
import RiferimentoSelect from '../RiferimentoSelect';
import MancatePresentazioni from './MancatePresentazioni';
import { annunciaAlertCambiati } from '../../utils/annunci';
import { notificaErrore, notificaAvviso, messaggioDa } from '../../utils/notifiche';

/* mostra la data ESATTAMENTE come salvata, senza conversione di fuso */
const fmtDT = (s) => {
    if (!s) return '—';
    const m = String(s).match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (!m) return String(s);
    const [, y, mo, d, h, mi] = m;
    return `${d}/${mo}/${y}, ${h}:${mi}`;
};

const PAGINA = 20;

const AlertTab = ({ pazienteId }) => {
    const [lista, setLista] = useState([]);
    const [loading, setLoading] = useState(true);
    const [titolo, setTitolo] = useState('');
    const [dataOra, setDataOra] = useState('');
    const [descrizione, setDescrizione] = useState('');
    const [rifTipo, setRifTipo] = useState(null);
    const [rifId, setRifId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [mostrati, setMostrati] = useState(PAGINA);

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [pazienteId]);

    const load = async () => {
        try {
            setLoading(true);
            const r = await alertAPI.getByPaziente(pazienteId);
            if (r.data.success) setLista(r.data.data.alert || []);
        } catch (e) { console.error('Errore caricamento alert', e); }
        finally { setLoading(false); }
    };

    const crea = async (e) => {
        e.preventDefault();
        if (!titolo.trim() || !dataOra) { notificaAvviso('Titolo e data/ora sono obbligatori'); return; }
        setSaving(true);
        try {
            await alertAPI.create(pazienteId, {
                titolo: titolo.trim(), data_ora: dataOra, descrizione: descrizione.trim(),
                riferimento_tipo: rifTipo, riferimento_id: rifId,
            });
            setTitolo(''); setDataOra(''); setDescrizione('');
            setRifTipo(null); setRifId(null);
            setMostrati(PAGINA);
            load(); annunciaAlertCambiati();
        } catch (e) { notificaErrore(messaggioDa(e, 'Non riesco a creare il promemoria')); }
        finally { setSaving(false); }
    };

    const toggleFatto = async (a) => {
        try { await alertAPI.update(a.id, { stato: a.stato === 'fatto' ? 'attivo' : 'fatto' }); load(); annunciaAlertCambiati(); }
        catch (e) { notificaErrore(messaggioDa(e, 'Non riesco ad aggiornare il promemoria')); }
    };

    const elimina = async (id) => {
        if (!window.confirm('Eliminare questo alert?')) return;
        try { await alertAPI.remove(id); load(); annunciaAlertCambiati(); }
        catch (e) { notificaErrore(messaggioDa(e, 'Non riesco a eliminare il promemoria')); }
    };

    const visibili = lista.slice(0, mostrati);

    return (
        <div className="al-wrap">
            {/* Sta qui e non in una linguetta sua: e' informazione su come
                il paziente si comporta con gli appuntamenti, come gli
                alert. Si nasconde da sola se non e' mai successo. */}
            <MancatePresentazioni pazienteId={pazienteId} />
            <form className="al-form" onSubmit={crea}>
                <h3>Nuovo Alert</h3>
                <div className="al-form-grid">
                    <label className="al-field">
                        <span>Titolo *</span>
                        <input value={titolo} onChange={e => setTitolo(e.target.value)} placeholder="Es: Richiamo controllo" />
                    </label>
                    <label className="al-field">
                        <span>Data e ora *</span>
                        <input type="datetime-local" value={dataOra} onChange={e => setDataOra(e.target.value)} />
                    </label>
                </div>
                <label className="al-field">
                    <span>Descrizione</span>
                    <textarea rows="2" value={descrizione} onChange={e => setDescrizione(e.target.value)} placeholder="Dettagli (opzionale)…" />
                </label>
                {/* Dove deve portare il promemoria quando lo si aprira'. Si
                    sceglie adesso, che si ha in mente di cosa si tratta, non
                    fra tre settimane leggendo solo il titolo. */}
                <RiferimentoSelect
                    pazienteId={pazienteId}
                    tipo={rifTipo}
                    id={rifId}
                    onChange={(t, i) => { setRifTipo(t); setRifId(i); }}
                    classe="al-field"
                />
                <button className="al-btn" disabled={saving}>{saving ? 'Salvataggio…' : 'Crea Alert'}</button>
            </form>

            <div className="al-log-title">Storico alert {lista.length > 0 && <span>({lista.length})</span>}</div>

            {loading ? <div className="sp-loading">Caricamento…</div> : (
                lista.length === 0 ? (
                    <p className="al-empty">Nessun alert per questo paziente.</p>
                ) : (
                    <>
                        <div className="al-list">
                            {visibili.map(a => (
                                <div key={a.id} className={`al-item ${a.stato === 'fatto' ? 'done' : ''}`}>
                                    <div className="al-item-main">
                                        <div className="al-item-top">
                                            <span className="al-item-dt">{fmtDT(a.data_ora)}</span>
                                            {a.stato === 'fatto' && <span className="al-badge">Fatto</span>}
                                        </div>
                                        <div className="al-item-titolo">{a.titolo}</div>
                                        {a.descrizione && <div className="al-item-desc">{a.descrizione}</div>}
                                    </div>
                                    <div className="al-item-actions">
                                        <button onClick={() => toggleFatto(a)} title={a.stato === 'fatto' ? 'Riattiva' : 'Segna fatto'}>
                                            {a.stato === 'fatto' ? '↺' : '✓'}
                                        </button>
                                        <button onClick={() => elimina(a.id)} title="Elimina"><Trash2 size={15} strokeWidth={1.8} aria-hidden="true" /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {mostrati < lista.length && (
                            <button className="al-more" onClick={() => setMostrati(m => m + PAGINA)}>
                                Mostra altri ({lista.length - mostrati})
                            </button>
                        )}
                    </>
                )
            )}
        </div>
    );
};

export default AlertTab;
